import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import ServerClock from "./serverClock";
import { getMaxXpForRank } from "../utils/xpUtils";
import useAuthUser from "../hooks/useAuthUser";
import PortfolioPanel from "./PortfolioPanel";
import "./tradingDashboard.css";
import DataStream from "./DataStream";
import ProfilePanel from "./ProfilePanel";
import PlayerProfilePanel from "./PlayerProfilePanel";
import History from "./History";
import BondsFutures from "./BondsFutures";
import Leaderboards from "./Leaderboards";
import AchievementsPanel from "./AchievementsPanel";
import { processUser } from "../utils/userUtils";
import currencyExchangeIcon from "../assets/icons/currencyExchange.png";
import marketVolatilityIcon from "../assets/icons/marketVolatility.png";
import referralPageIcon from "../assets/icons/referralPage.png";
import politicalStakingIcon from "../assets/icons/politicalPage.png";
import personalAssetsIcon from "../assets/icons/personalAssets.png";
import newsFeedIcon from "../assets/icons/newsFeed.png";
import CurrencyExchangeModal from "./CurrencyExchangeModal";
import MarketVolatilityModal from "./MarketVolatilityModal";
import ReferralPageModal from "./ReferralPageModal";
import WorkPanel from "./WorkPanel";
import WallPanel from "./WallPanel";
import { getAuth } from "firebase/auth";
import { getUserData } from "../services/userService";
import PopperPopup from "./PopperPopup";
import AchievementUnlockedModal from "./AchievementUnlockedModal";
import useDisplayNames from "../utils/useDisplayNames";
import PersonalChartModal from "./PersonalChart";
import { getUserCharts } from "../services/userService";
import { listenToUserData } from "../services/userService";
import LevelUpModal from "./LevelUpModal";
import Signal from './Signal';
import PoliticalChamber from "./PoliticalChamber";
import ItemCasePortal from "./ItemCase";
import packageJson from "../../package.json"
import NewsComposer from './NewsComposer';
import { createPortal } from 'react-dom';
import NewsFeedModal from "./NewsFeed";
import SettingsModal from "./SettingsModal";
import ConfirmModal from "./ConfirmModal";
import NewsButton from "./NewsButton";

export default function LeftSidebar({
  activeVote,
  companies,
  onLogout,
  selectedCompany,
  setSelectedCompany,
  selectedPlayer,
  setSelectedPlayer,
  playerSelected,
  currentUser,
  addNotification,
  calculatePortfolio,
  setShowNewsModal
}) {
  const [portfolioTab, setPortfolioTab] = useState("Portfolio");
  const [datastreamTab, setDatastreamTab] = useState("Datastream");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [showPersonalChart, setShowPersonalChart] = useState(false);
  const [personalChartData, setPersonalChartData] = useState(null);
  const [user, setUser] = useState(null);
  const [showRankUp, setShowRankUp] = useState(false);
  const [lastRank, setLastRank] = useState(null);
  const [isNewsComposerOpen, setIsNewsComposerOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmInsolvency, setShowConfirmInsolvency] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

const handleComboFinished = useCallback(async (xpGained, comboNumber) => {
  if (xpGained <= 0) return;

  try {
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken(true); 

    const response = await fetch("https://signalxp-5dhfwyjvzq-uc.a.run.app", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ 
        xpAmount: xpGained,
        combo: comboNumber,
        userDisplayName: user.displayName || "null-error",
        details: `Signal Intercept: ${xpGained} XP`
      })
    });

    const responseText = await response.text();

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error("Server returned non-JSON response:", responseText);
      return;
    }

    if (!response.ok) {
      console.error(`Sync failed (${response.status}):`, data.error || responseText);
      return;
    }

    console.log("Success:", data);
  } catch (error) {
    console.error("Network Error:", error);
  }
}, []);
  
const handleLinkDiscord = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return alert("Please log in first.");

  const DISCORD_CLIENT_ID = "1357211447473733732";
  const REDIRECT_URI = encodeURIComponent(
    "https://us-central1-stockmarket-83a73.cloudfunctions.net/verifyDiscordCallback"
  );
  const state = user.uid;
  const scope = encodeURIComponent("identify email");

  const oauthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=${scope}&state=${state}`;

  window.location.href = oauthUrl;
};

  const [openModals, setOpenModals] = useState({
    currency: false,
    volatility: false,
    referral: false,
  });

  const rawUserData = useAuthUser();
  const userData = useMemo(() => {
    try {
      return processUser(JSON.parse(JSON.stringify(rawUserData)));
    } catch {
      return null;
    }
  }, [rawUserData]);

const { players: allPlayers, loading: searchLoading } = useDisplayNames();
  const inputRef = useRef(null);

  const openModal = (name) =>
    setOpenModals((prev) => ({ ...prev, [name]: true }));

  const closeModal = (name) =>
    setOpenModals((prev) => ({ ...prev, [name]: false }));

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setSearchResults([]);
      setHighlightIndex(-1);
      return;
    }
    const matches = allPlayers.filter((p) =>
      p.displayName.toLowerCase().startsWith(term)
    );
    setSearchResults(matches.slice(0, 8));
    setHighlightIndex(matches.length ? 0 : -1);
  }, [searchTerm, allPlayers]);

const rankTable = [
  { rank: 20, xp: 35000000 },
  { rank: 19, xp: 20000000 },
  { rank: 18, xp: 13000000 },
  { rank: 17, xp: 8500000 },
  { rank: 16, xp: 5500000 },
  { rank: 15, xp: 3500000 },
  { rank: 14, xp: 2250000 },
  { rank: 13, xp: 1500000 },
  { rank: 12, xp: 1000000 },
  { rank: 11, xp: 750000 },
  { rank: 10, xp: 500000 },
  { rank: 9, xp: 330000 },
  { rank: 8, xp: 200000 },
  { rank: 7, xp: 110000 },
  { rank: 6, xp: 60000 },
  { rank: 5, xp: 30000 },
  { rank: 4, xp: 14000 },
  { rank: 3, xp: 6000 },
  { rank: 2, xp: 2000 }
];

const currentRankObj = rankTable.find(r => r.rank === rawUserData?.userRank);
const currentRankFloor = currentRankObj ? currentRankObj.xp : 0;

const nextRankCeiling = getMaxXpForRank(rawUserData?.userRank);

const xpInCurrentLevel = (rawUserData?.userXP || 0) - currentRankFloor;
const xpNeededForCurrentLevel = nextRankCeiling - currentRankFloor;

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,

  backgroundColor: '#000000f2',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 999999,
  backdropFilter: 'blur(8px)',
};

const closeButtonStyle = {
  position: 'absolute',
  top: '-40px',
  right: '0',
  background: 'transparent',
  border: '1px solid #ff4444',
  color: '#ff4444',
  padding: '4px 8px',
  cursor: 'pointer',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  zIndex: 1000001
};

const xpPercent = xpNeededForCurrentLevel > 0
  ? Math.min(Math.max((xpInCurrentLevel / xpNeededForCurrentLevel) * 100, 0), 100)
  : 100;

const xpRemaining = Math.max(nextRankCeiling - (rawUserData?.userXP || 0), 0);

useEffect(() => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  (async () => {
    const data = await getUserData(user.uid);
    setCurrentUserData(data);
    
  })();
}, []);

useEffect(() => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  const unsubscribe = listenToUserData(user.uid, (data) => {
    setUser(data);

    if (lastRank !== null && data.userRank > lastRank) {
      setShowRankUp(true);
    }

    setLastRank(data.userRank);
  });

  return () => unsubscribe();
}, [lastRank]);

  const handleLogout = useCallback(() => {
    onLogout?.();
  }, [onLogout]);

  const openPlayerModal = useCallback(
    (name) => {
      if (!name) return;
      setSelectedPlayer(name);
      setSearchTerm("");
      setSearchResults([]);
      setHighlightIndex(-1);
    },
    [setSelectedPlayer]
  );

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const picked =
        searchResults[highlightIndex]?.displayName ||
        searchResults[0]?.displayName ||
        searchTerm.trim();
      openPlayerModal(picked);
    },
    [searchResults, highlightIndex, searchTerm, openPlayerModal]
  );

const [confirmConfig, setConfirmConfig] = useState({
  isOpen: false,
  title: "",
  message: "",
  confirmText: "",
  cancelText: "CANCEL",
  onConfirm: () => {},
  onCancel: () => {}
});

const closeConfirm = () => setConfirmConfig(prev => ({ ...prev, isOpen: false }));

const handleUpgradeLevel = async () => {
  setIsProcessing(true);
  
  try {
    // 1. Explicitly define and fetch the Global_Vars/Main document
    const docRef = doc(db, "Global_Vars", "Main");
    const globalSnap = await getDoc(docRef);
    
    if (!globalSnap.exists()) {
      throw new Error("System configuration variables missing.");
    }

    const globalData = globalSnap.data();
    const baseTaxRate = globalData.globalBaseTaxRate || 0;
    const calculatedGoldCost = Math.round(Math.log2((baseTaxRate / 100) * 150 + 1) * 300);

    setConfirmConfig({
      isOpen: true,
      title: "UPGRADE LEVEL",
      message: `Are you sure you want to upgrade your trader level to Preferred for ${calculatedGoldCost} Gold? This tier will remain active for 30 days.`,
      confirmText: "PROCEED",
      cancelText: "CANCEL",
      onCancel: () => {
        closeConfirm();
        setIsProcessing(false);
      },
      onConfirm: async () => {
        closeConfirm();
        
        try {
          const user = auth.currentUser;
          if (!user) throw new Error("No authenticated session found.");
          
          const freshToken = await user.getIdToken(true);

          const response = await fetch('https://us-central1-stockmarket-83a73.cloudfunctions.net/upgradeLevel', {
            method: 'POST',
            mode: 'cors',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${freshToken}`
            }
          });

          const data = await response.json();

          if (!response.ok || data.error) {
            throw new Error(data.error || "TRANSACTION REFUSED");
          }

          setConfirmConfig({
            isOpen: true,
            title: "SUCCESS",
            message: `${data.message.toUpperCase()} CHARGED: ${data.goldCharged || calculatedGoldCost} GOLD.`,
            confirmText: "ACKNOWLEDGE",
            cancelText: "",
            onCancel: closeConfirm,
            onConfirm: closeConfirm
          });

        } catch (err) {
          console.error("Upgrade Execution Error:", err);
          setConfirmConfig({
            isOpen: true,
            title: "TRANSACTION REJECTED",
            message: `UPGRADE FAILED: ${err.message.toUpperCase()}`,
            confirmText: "RETURN TO TERMINAL",
            cancelText: "",
            onCancel: closeConfirm,
            onConfirm: closeConfirm
          });
        } finally {
          setIsProcessing(false);
        }
      }
    });

  } catch (err) {
    console.error("Initialization Error:", err);
    setConfirmConfig({
      isOpen: true,
      title: "SYSTEM DATA FAULT",
      message: `COULD NOT COMPUTE PRICE: ${err.message.toUpperCase()}`,
      confirmText: "OK",
      cancelText: "",
      onCancel: closeConfirm,
      onConfirm: closeConfirm
    });
    setIsProcessing(false);
  }
};
  
const handleDeclareInsolvency = async () => {
  if (isProcessing) return;

  setIsProcessing(true);

  try {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch("https://declareinsolvency-5dhfwyjvzq-uc.a.run.app", {
      method: "POST",
      headers: { 
        "Content-Type": "application/json", 
        Authorization: `Bearer ${token}` 
      }
    });

    const data = await res.json();

    if (data?.success) {
      addNotification("Insolvency declared. Balance reset.", "success");
      setShowConfirmInsolvency(false);
      await calculatePortfolio(); 
    } else {
      addNotification(data?.message || "Failed to declare insolvency.", "error");
      setIsProcessing(false);
    }
  } catch (err) {
    console.error(err);
    addNotification("Network error.", "error");
    setIsProcessing(false);
  }
};

const renderTabButton = (label, currentTab, setTab) => {
  const isActive = currentTab === label;
  
  return (
    <div 
      className={`nav-module ${isActive ? "active" : ""}`} 
      onClick={() => setTab(label)}
    >
      <span className="module-label">
        {label === "Bonds & Futures" ? "Bonds & Futures" : label}
      </span>
    </div>
  );
};

  const stablePlayerSelected = useMemo(() => {
    if (!selectedPlayer) return null;
    return typeof selectedPlayer === "string"
      ? selectedPlayer
      : selectedPlayer.userDisplayName || selectedPlayer.name || null;
  }, [selectedPlayer]);

  const handleCloseProfile = useCallback(() => {
    setSelectedPlayer(null);
  }, [setSelectedPlayer]);

  if (!userData) {
    return (
      <aside id="left-sidebar">
        <div>Loading...</div>
      </aside>
    );
  }

const BIO_URL = "https://updatebio-5dhfwyjvzq-uc.a.run.app";

const handleUpdateUserBio = async (text) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const idToken = await currentUser.getIdToken(true);

    const response = await fetch(BIO_URL, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body: JSON.stringify({ bio: text }),
    });

    const rawData = await response.text();
    let data;

    try {
      data = JSON.parse(rawData);
    } catch (e) {
      data = { message: rawData };
    }

    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to update summary.");
    }

    console.log("Update successful:", data.message);


  } catch (error) {
    console.error("Summary Update Error:", error.message);
  }
};

const formatCurrency = (val) => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const formatNumber = (val) => 
  new Intl.NumberFormat('en-US').format(val || 0);

  const regionMap = {
    false: "Global",
    AF: "Africa",
    AS: "Asia",
    AU: "Australia",
    EU: "Europe",
    NA: "North America",
    SA: "South America",
  };

return (
    <>
      <aside id="left-sidebar" style={{ fontFamily: "'JetBrains Mono', monospace", display: "flex", flexDirection: "column", gap: "12px" }}>

        <section id="username" style={{ background: "rgba(0, 20, 20, 0.4)", padding: "12px", border: "1px solid rgba(80, 227, 194, 0.1)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
            <div>
              <div style={{ color: "#50e3c2", fontSize: "0.55rem", letterSpacing: "1.5px" }}>
  OUTTRADE ONLINE v{packageJson.version}
</div>
              <h2
                onClick={async () => {
                  if (rawUserData.userLevel !== "Basic") {
                    try {
                      setPersonalChartData(null);
                      setShowPersonalChart(true);
                      const charts = await getUserCharts();
                      setPersonalChartData(charts);
                    } catch (err) {
                      console.error("Error fetching user charts:", err);
                      setShowPersonalChart(false);
                    }
                  }
                }}
                style={{ 
                  cursor: rawUserData.userLevel !== "Basic" ? "pointer" : "default",
                  margin: 0, fontSize: "1.3rem", color: "#fff",
                  textShadow: rawUserData.userLevel !== "Basic" ? "0 0 10px rgba(80, 227, 194, 0.3)" : "none"
                }}
              >
                {rawUserData.userDisplayName}
              </h2>
            </div>

<>
<PopperPopup
  popupContent={
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", background: "#0f1515", padding: "10px", border: "1px solid #50e3c2" }}>
      <button onClick={() => setActiveModal('EMAIL')} className="popper-button">
        UPDATE EMAIL
      </button>
      <button onClick={() => setActiveModal('PASSWORD')} className="popper-button">
        UPDATE PASSWORD
      </button>

      {(!currentUserData?.userLevel || currentUserData?.userLevel.toString().toLowerCase() === "basic") && (
        <button 
          onClick={handleUpgradeLevel}
          disabled={isProcessing}
          className="popper-button"
          style={{ border: '1px solid #FFD700', color: '#FFD700', fontWeight: 'bold' }}
        >
          {isProcessing ? "UPGRADE TRADER LEVEL" : "UPGRADE TRADER LEVEL"}
        </button>
      )}

      {currentUserData?.discordLinkedAt ? (
        <div style={{ color: "#50e3c2", fontWeight: 500, fontSize: '12px', padding: '4px' }}>
          Discord Linked ({currentUserData.discordUsername})
        </div>
      ) : (
        <button onClick={handleLinkDiscord} className="popper-button discord">Link Discord</button>
      )}

      {currentUserData?.userMoney < -1000 && (
        <button 
          onClick={() => setShowConfirmInsolvency(true)} 
          disabled={isProcessing}
          className="popper-button"
          style={{ border: '1px solid #ff4d4d', color: '#ff4d4d' }}
        >
          {isProcessing ? "PROCESSING..." : "Declare Insolvency"}
        </button>
      )}

      {currentUserData?.userDisplayName === "Tony" && (
        <button 
          onClick={() => setIsNewsComposerOpen(true)} 
          className="popper-button"
          style={{ border: '1px solid #b2ffef', color: '#b2ffef' }}
        >
          Write News
        </button>
      )}
      
      <button onClick={handleLogout} className="popper-button">Logout</button>
    </div>
  }
>
  <button className="settings-button">⚙ Settings</button>
</PopperPopup>

    {confirmConfig.isOpen && (
      <ConfirmModal
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText || undefined}
        onConfirm={confirmConfig.onConfirm}
        onCancel={confirmConfig.onCancel}
      />
    )}
      {activeModal && (
        <SettingsModal 
          type={activeModal} 
          userToken={userData.token}
          onClose={() => setActiveModal(null)} 
        />
      )}
      
  {showConfirmInsolvency && createPortal(
  <div style={{ 
    position: "fixed", 
    inset: 0, 
    background: "rgba(0, 10, 10, 0.95)", 
    backdropFilter: 'blur(8px)', 
    display: "flex", 
    flexDirection: "column", 
    justifyContent: "center", 
    alignItems: "center", 
    padding: "24px", 
    zIndex: 99999
  }}>
    <div style={{ 
      background: "#0f1515", 
      padding: "30px", 
      border: "2px solid #ff4d4d", 
      boxShadow: "0 0 20px rgba(255, 77, 77, 0.2)",
      textAlign: "center",
      maxWidth: "400px",
      width: "90%"
    }}>
      <div style={{ color: "#fff", marginBottom: "20px", fontSize: "0.9rem", lineHeight: "1.6" }}>
        <span style={{ color: '#ff4d4d', fontWeight: 'bold', fontSize: '1.2rem' }}>DECLARE INSOLVENCY?</span><br/><br/>
        This will liquidate all holdings and resolve your wallet balanace to $0.00. This action is irreversible.
      </div>
      
      <div style={{ display: "flex", gap: "12px", width: "100%" }}>
        <button 
          onClick={() => setShowConfirmInsolvency(false)} 
          disabled={isProcessing}
          style={{ 
            flex: 1, padding: "12px", background: "transparent", color: "#888", 
            border: "1px solid #333", cursor: "pointer", fontFamily: "inherit" 
          }}
        >
          CANCEL
        </button>
        <button 
          onClick={handleDeclareInsolvency} 
          disabled={isProcessing}
          style={{ 
            flex: 1, padding: "12px", background: "#ff4d4d", color: "#000", 
            border: "none", fontWeight: "bold", cursor: isProcessing ? "not-allowed" : "pointer",
            fontFamily: "inherit"
          }}
        >
          {isProcessing ? "DECLARING..." : "CONFIRM"}
        </button>
      </div>
    </div>
  </div>,
  document.body
)}
</>

{isNewsComposerOpen && createPortal(
  <div style={modalOverlayStyle}>
    <div style={{ position: 'relative', zIndex: 10001 }}>
      <button 
        onClick={() => setIsNewsComposerOpen(false)} 
        style={closeButtonStyle}
      >
        [X] CLOSE
      </button>
      <NewsComposer />
    </div>
  </div>,
  document.body
)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.3)", padding: "6px 10px", borderLeft: "2px solid #F4C542" }}>
              <span style={{ color: "#666", fontSize: "0.55rem" }}>PORTFOLIO</span>
              <span style={{ color: "#F4C542", fontSize: "0.95rem", fontWeight: "bold" }}>
                {Number(rawUserData.userPortfolioValue).toLocaleString("en-US", { style: "currency", currency: "USD" })}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.3)", padding: "6px 10px", borderLeft: "2px solid #7ED957" }}>
              <span style={{ color: "#666", fontSize: "0.55rem" }}>WALLET</span>
              <span style={{ color: "#7ED957", fontSize: "0.95rem", fontWeight: "bold" }}>
                {Number(rawUserData.userMoney).toLocaleString("en-US", { style: "currency", currency: "USD" })}
              </span>
            </div>
          </div>

          <div style={{ marginTop: "12px" }}>
            <div style={{ height: "4px", background: "rgba(255,255,255,0.05)", position: "relative", marginBottom: "6px" }}>
              <div style={{ width: `${xpPercent}%`, height: "100%", background: "#50e3c2", boxShadow: "0 0 8px rgba(80, 227, 194, 0.4)" }}></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <small style={{ color: "#666", fontSize: "0.6rem" }}>
                {Intl.NumberFormat().format(Math.round(rawUserData.userXP))} EXP 
                {rawUserData.userRank < 20 && ` (${Intl.NumberFormat().format(Math.round(xpRemaining))} REMAINING)`}
              </small>
              <ServerClock />
            </div>
          </div>
        </section>

        <section id="portfolio" className="tabbed-panel">
          <div className="tab-navigation-container">
            <div className="nav-modules-row">
              {renderTabButton("Portfolio", portfolioTab, setPortfolioTab)}
              {renderTabButton("Profile", portfolioTab, setPortfolioTab)}
              {renderTabButton("History", portfolioTab, setPortfolioTab)}
              {renderTabButton("Bonds & Futures", portfolioTab, setPortfolioTab)}
              {renderTabButton("Leaderboards", portfolioTab, setPortfolioTab)}
              {renderTabButton("Achievements", portfolioTab, setPortfolioTab)}
            </div>
          </div>

          <div className="tab-content">
            {portfolioTab === "Portfolio" && (
              <PortfolioPanel selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany} />
            )}
{portfolioTab === "Profile" && (
  <ProfilePanel 
    profile={user} 
    onUpdateBio={handleUpdateUserBio}
    formatCurrency={formatCurrency}
    formatNumber={formatNumber}
    regionMap={regionMap}
  />
)}
            {portfolioTab === "History" && <History />}
            {portfolioTab === "Bonds & Futures" && <BondsFutures />}
            {portfolioTab === "Leaderboards" && (
              <Leaderboards setSelectedCompany={setSelectedCompany} setSelectedPlayer={setSelectedPlayer} />
            )}
            {portfolioTab === "Achievements" && <AchievementsPanel />}
          </div>
        </section>

        <section id="player-search" style={{ position: "relative", zIndex: 2000, display: "flex", justifyContent: "center", gap: "6px" }}>
<form onSubmit={handleSearchSubmit} style={{ display: "flex", gap: "6px", alignItems: "center" }}>
{userData.rank >= 6 && (
  <img 
    src={politicalStakingIcon} 
    className="sidebar-icon" 
    onClick={() => openModal("staking")} 
    style={{ cursor: "pointer" }} 
    title="Politics"
    alt="Open Politics"
  />
)}
<NewsButton 
  newsFeedIcon={newsFeedIcon} 
  openModal={openModal} 
/>
<img 
  src={personalAssetsIcon} 
  className="sidebar-icon" 
  onClick={() => openModal("personalAssets")}
  style={{ cursor: "pointer" }} 
  title="Personal Assets"
  alt="Open Personal Assets"
/>
  <input
    ref={inputRef}
    type="text"
    placeholder={searchLoading ? "LOADING..." : "SEARCH TRADER..."}
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    style={{
      width: "150px", 
      padding: "8px 10px", 
      borderRadius: "0px",
      border: "1px solid #2c3e3e",
      fontSize: "12px", 
      background: "#0f1515", 
      color: "#F8F9FA", 
      outline: "none",
      boxSizing: "border-box",
      fontFamily: "'JetBrains Mono', monospace" 
    }}
  />

  <img 
    src={currencyExchangeIcon} 
    className="sidebar-icon" 
    onClick={() => openModal("currency")} 
    style={{ cursor: "pointer" }} 
    title="Currency Exchange"
    alt="Open Currency Exchange"
  />

  <img 
    src={marketVolatilityIcon} 
    className="sidebar-icon" 
    onClick={() => openModal("volatility")} 
    style={{ cursor: "pointer" }} 
    title="Market Volatility"
    alt="View Market Volatility Index"
  />

  <img 
    src={referralPageIcon} 
    className="sidebar-icon" 
    onClick={() => openModal("referral")} 
    style={{ cursor: "pointer" }} 
    title="Referrals"
    alt="Open Referral Management"
  />
</form>

          {searchResults.length > 0 && inputRef.current && (
            <ul style={{
              listStyle: "none", position: "absolute", top: inputRef.current.offsetTop + inputRef.current.offsetHeight + 4,
              left: inputRef.current.offsetLeft, width: inputRef.current.offsetWidth, background: "#0f1515",
              border: "1px solid #2c3e3e", borderRadius: "8px", maxHeight: 200, overflowY: "auto", zIndex: 2001
            }}>
              {searchResults.map((p, idx) => (
                <li key={p.id} onClick={() => openPlayerModal(p.displayName)}
                  style={{ padding: "8px 10px", cursor: "pointer", color: "#F8F9FA", background: highlightIndex === idx ? "#183c3c" : "transparent" }}>
                  {p.displayName}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section id="datastream" className="tabbed-panel">
          <div className="tab-header">
            <div className="nav-modules-row">
              {renderTabButton("DataStream", datastreamTab, setDatastreamTab)}
              {renderTabButton("Wall", datastreamTab, setDatastreamTab)}
              {renderTabButton("Signal", datastreamTab, setDatastreamTab)}
              {renderTabButton("Work", datastreamTab, setDatastreamTab)}
            </div>
          </div>

          <div className="tab-content" style={{ height: '300px', position: 'relative' }}>
            {datastreamTab === "DataStream" && <DataStream />}
            {datastreamTab === "Wall" && <WallPanel />}
            {datastreamTab === "Signal" && <Signal userRank={userData.rank} onComboEnd={handleComboFinished} />}
            {datastreamTab === "Work" && <WorkPanel />}
          </div>
        </section>

        {showPersonalChart && <PersonalChartModal onClose={() => setShowPersonalChart(false)} chartData={personalChartData} userName={rawUserData.userDisplayName} />}
        {stablePlayerSelected && <PlayerProfilePanel playerSelected={stablePlayerSelected} setSelectedPlayer={setSelectedPlayer} onClose={handleCloseProfile} onUpdateBio={(text) => handleUpdateUserBio(text)} />}
        <AchievementUnlockedModal />
      </aside>

{openModals.currency && <CurrencyExchangeModal onClose={() => closeModal("currency")} prices={Object.fromEntries((companies || []).filter(i => ["AUD", "BRL", "CNY", "EUR", "ZAR"].includes(i.companyTicker)).map(i => [i.companyTicker, i.companyStockPrice]))} />}
      {openModals.volatility && <MarketVolatilityModal onClose={() => closeModal("volatility")} />}
      {openModals.referral && <ReferralPageModal onClose={() => closeModal("referral")} />}
      {openModals.news && <NewsFeedModal onClose={() => closeModal("news")} />}
      {openModals.personalAssets && (
  <ItemCasePortal 
    currentUser={currentUser} 
    isOpen={true} 
    onClose={() => closeModal("personalAssets")} 
  />
)}
      {openModals.staking && (
        <PoliticalChamber 
          userData={user} 
          currentUser={currentUser} 
          activeVote={activeVote} 
          onClose={() => setOpenModals({ ...openModals, staking: false })} 
        />
      )}
      {showRankUp && <LevelUpModal isOpen={showRankUp} onClose={() => setShowRankUp(false)} newRank={user?.userRank} newTitle={user?.userTitle} />}
    </>
  );
}
